/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { mergeStyles } from '@fluentui/merge-styles';
import { Callout, DirectionalHint, ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { IIconStyleProps, IIconStyles, Icon } from '@fluentui/react/lib/Icon';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { getFocusStyle, getTheme } from '@fluentui/style-utilities';
import * as React from 'react';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

export type CalloutButtonProps = {
    ariaLabel?: string;
    isDarkMode?: boolean;
    iconClassName?: string;
    content: React.ReactNode;
    containerClassName?: string;
};

const calloutTheme = getScopedTheme('Callout');
const { styleSpan, styleDiv } = getStylist('Callout');

const focusStyles = getFocusStyle(getTheme(), { inset: -2 });
const InlineDiv = styleDiv('InlineDiv', { display: 'inline-flex' });
// Icon sets aria-hidden to false which causes an issue with a11y, container needed.
const IconContainer = styleSpan('IconContainer', { display: 'flex', margin: '0 7px' });
const Description = styleDiv('Description', { overflow: 'hidden', maxWidth: 350, padding: 8 });

const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: {
        fontSize: 14,
        cursor: 'pointer',
        lineHeight: '14px',
        color: calloutTheme.buttonColor,
        borderRadius: calloutTheme.buttonBorderRadius,
        '&:hover': { backgroundColor: calloutTheme.hoveredButtonBackground, color: calloutTheme.buttonColor }
    }
});

export const CalloutButton = (props: CalloutButtonProps) => {
    const { content, containerClassName, ariaLabel = '', isDarkMode = true } = props;
    const [targetId] = React.useState(getId());
    const [descriptionId] = React.useState(getId());
    const iconContainerRef = React.useRef<HTMLDivElement>();
    const [isCalloutHidden, setIsCalloutHidden] = React.useState(true);

    const calloutStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
        beak: { backgroundColor: isDarkMode ? calloutTheme.darkBackgroundColor : calloutTheme.lightBackgroundColor },
        beakCurtain: { backgroundColor: isDarkMode ? calloutTheme.darkBackgroundColor : calloutTheme.lightBackgroundColor },
        calloutMain: {
            backgroundColor: isDarkMode ? calloutTheme.darkBackgroundColor : calloutTheme.lightBackgroundColor,
            '> *': {
                lineHeight: '1.5',
                color: isDarkMode ? calloutTheme.darkTextColor : calloutTheme.lightTextColor
            }
        }
    });

    const getIconAriaLabel = () => {
        if (ariaLabel) {
            return ariaLabel;
        }
        if (typeof content === 'string') {
            return content;
        }
        return '';
    };

    const onIconClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsCalloutHidden(false);
    };

    return (
        <InlineDiv id={targetId} className={containerClassName} onMouseLeave={() => setIsCalloutHidden(true)}>
            <IconContainer
                tabIndex={0}
                data-is-focusable
                originalRef={iconContainerRef}
                onKeyDown={e => {
                    if (e.key === LuisKeyCodes.Enter) {
                        setIsCalloutHidden(!isCalloutHidden);
                    }
                }}
                className={mergeStyles(focusStyles)}
                onMouseEnter={() => setIsCalloutHidden(false)}>
                <Icon
                    role="button"
                    iconName="Info"
                    styles={iconStyles}
                    onClick={onIconClick}
                    aria-describedby={targetId}
                    aria-label={getIconAriaLabel()}
                />
            </IconContainer>

            <Callout
                gapSpace={0}
                setInitialFocus
                role="alertdialog"
                styles={calloutStyles}
                target={`#${targetId}`}
                hidden={isCalloutHidden}
                onKeyDown={evt => {
                    if (evt.key === LuisKeyCodes.Tab) {
                        setIsCalloutHidden(true);
                        iconContainerRef.current.focus();
                    }
                }}
                onDismiss={() => {
                    setIsCalloutHidden(true);
                    iconContainerRef.current.focus();
                }}
                directionalHint={DirectionalHint.topCenter}>
                <FocusZone>
                    <Description id={descriptionId}>{content}</Description>
                </FocusZone>
            </Callout>
        </InlineDiv>
    );
};
